import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useDebounce } from '@client/hooks/use-debounce';
import { useInfluencerListModel } from '../model/InfluencerList.model';
import { useAdminCreatorsPageModel } from '../model/adminCreatorsPage.model';
import { Influencer, InfluencerListModel } from '../model/InfluencerList.types';
import {
  filterBySearch,
  InfluencerSortField,
  InfluencerSorting,
  sortInfluencers,
  toAvatarUrl,
  toBusinessModelLabel,
  toDateLabel,
  toFullName,
  toggleSort,
} from './InfluencerList.viewModel.mappers';
import {
  FilterRule,
  FilterFieldConfig,
  FILTER_FIELD_CONFIGS,
} from './InfluencerList.filterTypes';
import { applyAllFilterRules } from './InfluencerList.filterEngine';
import {
  FilterPreset,
  loadFilterPresets,
  loadFilters,
  saveFilterPresets,
  loadUseOldList,
  saveFilters,
  saveUseOldList,
} from './InfluencerList.storage';
import { toServerRules } from './InfluencerList.serverRules';

export type InfluencerRowVM = {
  id: number;
  fullName: string;
  truncatedName: string;
  customURL: string;
  avatarUrl: string;
  businessModelLabel: string;
  tier: number | null;
  createdAtLabel: string;
  publishedAtLabel: string;
  instagramFollowersCount: number | null;
  profileInstagram: string | null;
  profileTiktok: string | null;
  profileYoutubeChannel: string | null;
  email: string | null;
  isTierUpdating: boolean;
  isDeleting: boolean;
};

type InfluencerListActionMap = {
  SetSearch: string;
  AddFilterRule: FilterRule;
  RemoveFilterRule: string;
  UpdateFilterRule: FilterRule;
  ClearAllFilters: null;
  ToggleSort: InfluencerSortField;
  DeleteInfluencer: number;
  SaveFilterPreset: string;
  LoadFilterPreset: string;
  DeleteFilterPreset: string;
};

export type InfluencerListTableVM = {
  filteredSorted: Influencer[];
  totalCount: number;
  visibleCount: number;
};

export type InfluencerListVM = {
  tableState: Async<InfluencerListTableVM>;
  toRowVM: (influencer: Influencer) => InfluencerRowVM;
  search: string;
  filterRules: FilterRule[];
  filterFieldConfigs: FilterFieldConfig[];
  filterPresets: FilterPreset[];
  sorting: InfluencerSorting;
  updateTier: (p: { influencerId: number; tier: number }) => Promise<void>;
  loadMore: () => void;
} & Dispatchers<InfluencerListActionMap>;

type RowActionsModel = Pick<
  InfluencerListModel,
  'updateTier' | 'deleteInfluencer'
>;

const MAX_NAME_LENGTH = 30;

const toTruncatedName = (fullName: string) =>
  fullName.length > MAX_NAME_LENGTH
    ? `${fullName.substring(0, MAX_NAME_LENGTH)}...`
    : fullName;

const useListControls = () => {
  const stored = useMemo(loadFilters, []);

  const [search, setSearch] = useState(stored.search ?? '');
  const [filterRules, setFilterRules] = useState<FilterRule[]>(
    stored.filterRules ?? [],
  );
  const [sorting, setSorting] = useState<InfluencerSorting>(
    stored.sorting ?? { field: 'name', direction: 'asc' },
  );
  const [filterPresets, setFilterPresets] =
    useState<FilterPreset[]>(loadFilterPresets);
  const debouncedSearch = useDebounce(search, 200);

  useEffect(() => {
    saveFilters({ search, filterRules, sorting });
  }, [search, filterRules, sorting]);

  return {
    search,
    setSearch,
    debouncedSearch,
    filterRules,
    setFilterRules,
    sorting,
    setSorting,
    filterPresets,
    setFilterPresets,
  };
};

const useListViewModel = (
  controls: ReturnType<typeof useListControls>,
  asyncModel: Async<RowActionsModel>,
  tableState: Async<InfluencerListTableVM>,
  loadMore: () => void,
) => {
  const {
    search,
    setSearch,
    filterRules,
    setFilterRules,
    sorting,
    setSorting,
    filterPresets,
    setFilterPresets,
  } = controls;
  const [updatingTierIds, setUpdatingTierIds] = useState<number[]>([]);
  const [deletingIds, setDeletingIds] = useState<number[]>([]);

  const asyncModelRef = useRef(asyncModel);
  asyncModelRef.current = asyncModel;

  const updateTier = useCallback(
    async (p: { influencerId: number; tier: number }): Promise<void> => {
      if (asyncModelRef.current.type !== 'fetched') return;

      setUpdatingTierIds((prev) => [...prev, p.influencerId]);

      try {
        await asyncModelRef.current.value.updateTier(p);
        toast.success('Tier updated successfully');
      } catch {
        toast.error('Something went wrong while updating tier');
      } finally {
        setUpdatingTierIds((prev) =>
          prev.filter((currentId) => currentId !== p.influencerId),
        );
      }
    },
    [],
  );

  const deleteInfluencer = useCallback(
    async (influencerId: number): Promise<void> => {
      if (asyncModelRef.current.type !== 'fetched') return;

      setDeletingIds((prev) => [...prev, influencerId]);

      try {
        await asyncModelRef.current.value.deleteInfluencer(influencerId);
        toast.success('Creator removed');
      } catch {
        toast.error('Could not delete creator');
      } finally {
        setDeletingIds((prev) =>
          prev.filter((currentId) => currentId !== influencerId),
        );
      }
    },
    [],
  );

  const _dispatch = (action: Actions<InfluencerListActionMap>) => {
    switch (action.type) {
      case 'SetSearch':
        setSearch(action.payload);
        return;

      case 'AddFilterRule':
        setFilterRules((prev) => [...prev, action.payload]);
        return;

      case 'RemoveFilterRule':
        setFilterRules((prev) =>
          prev.filter((rule) => rule.id !== action.payload),
        );
        return;

      case 'UpdateFilterRule':
        setFilterRules((prev) =>
          prev.map((rule) =>
            rule.id === action.payload.id ? action.payload : rule,
          ),
        );
        return;

      case 'ClearAllFilters':
        setFilterRules([]);
        return;

      case 'ToggleSort':
        setSorting((prev) => toggleSort(prev, action.payload));
        return;

      case 'DeleteInfluencer':
        void deleteInfluencer(action.payload);
        return;

      case 'SaveFilterPreset': {
        const preset: FilterPreset = {
          id: crypto.randomUUID(),
          name: action.payload,
          filters: { search, filterRules, sorting },
        };
        setFilterPresets((prev) => {
          const updated = [...prev, preset];
          saveFilterPresets(updated);
          return updated;
        });
        return;
      }

      case 'LoadFilterPreset': {
        const preset = filterPresets.find((p) => p.id === action.payload);
        if (!preset) return;
        setSearch(preset.filters.search);
        setFilterRules(preset.filters.filterRules);
        setSorting(preset.filters.sorting);
        return;
      }

      case 'DeleteFilterPreset': {
        setFilterPresets((prev) => {
          const updated = prev.filter((p) => p.id !== action.payload);
          saveFilterPresets(updated);
          return updated;
        });
        return;
      }
    }
  };

  const dispatchers = getDispatchers<InfluencerListActionMap>(_dispatch);

  const toRowVM = useCallback(
    (influencer: Influencer): InfluencerRowVM => {
      const fullName = toFullName(influencer);
      const safeFullName = fullName || 'Unnamed creator';

      return {
        id: influencer.id,
        fullName: safeFullName,
        truncatedName: toTruncatedName(safeFullName),
        customURL: influencer.customURL,
        avatarUrl: toAvatarUrl(influencer.mediaCoverPath),
        businessModelLabel: toBusinessModelLabel(influencer.businessModel),
        tier: influencer.tier,
        createdAtLabel: toDateLabel(influencer.createdAt),
        publishedAtLabel: toDateLabel(influencer.publishedAt),
        instagramFollowersCount: influencer.instagramFollowersCount,
        profileInstagram: influencer.profileInstagram,
        profileTiktok: influencer.profileTiktok,
        profileYoutubeChannel: influencer.profileYoutubeChannel,
        email: influencer.email,
        isTierUpdating: updatingTierIds.includes(influencer.id),
        isDeleting: deletingIds.includes(influencer.id),
      };
    },
    [updatingTierIds, deletingIds],
  );

  return toAsyncFetched<InfluencerListVM>({
    tableState,
    toRowVM,
    search,
    filterRules,
    filterFieldConfigs: FILTER_FIELD_CONFIGS,
    filterPresets,
    sorting,
    updateTier,
    loadMore,
    ...dispatchers,
  });
};

const noop = () => undefined;

export const useInfluencerListViewModel = () => {
  const controls = useListControls();
  const asyncModel = useInfluencerListModel();

  const tableState = useMemo((): Async<InfluencerListTableVM> => {
    if (asyncModel.type !== 'fetched') return asyncModel;

    const searchFiltered = filterBySearch(
      asyncModel.value.influencers,
      controls.debouncedSearch,
    );
    const filteredSorted = sortInfluencers(
      applyAllFilterRules(searchFiltered, controls.filterRules),
      controls.sorting,
    );

    return toAsyncFetched({
      filteredSorted,
      totalCount: asyncModel.value.influencers.length,
      visibleCount: filteredSorted.length,
    });
  }, [
    asyncModel,
    controls.debouncedSearch,
    controls.filterRules,
    controls.sorting,
  ]);

  return useListViewModel(controls, asyncModel, tableState, noop);
};

export const usePagedInfluencerListViewModel = () => {
  const controls = useListControls();
  const rules = useMemo(
    () => toServerRules(controls.filterRules, new Date()),
    [controls.filterRules],
  );
  const asyncModel = useAdminCreatorsPageModel({
    search: controls.debouncedSearch,
    rules,
    sort: controls.sorting,
  });

  const tableState = useMemo((): Async<InfluencerListTableVM> => {
    if (asyncModel.type !== 'fetched') return asyncModel;

    return toAsyncFetched({
      filteredSorted: asyncModel.value.influencers,
      totalCount: asyncModel.value.totalCount,
      visibleCount: asyncModel.value.filteredCount,
    });
  }, [asyncModel]);

  const loadMore = () => {
    if (asyncModel.type !== 'fetched') return;
    asyncModel.value
      .loadMore()
      .catch(() => toast.error('Could not load more creators'));
  };

  return useListViewModel(controls, asyncModel, tableState, loadMore);
};

export const useListMode = () => {
  const [useOldList, setUseOldList] = useState(loadUseOldList);

  const toggle = useCallback(() => {
    setUseOldList((prev) => {
      saveUseOldList(!prev);
      return !prev;
    });
  }, []);

  return { useOldList, toggle };
};

export type ListModeVM = ReturnType<typeof useListMode>;
